import { useState, useCallback, useRef, useEffect } from 'react';
import { useAppDispatch, useAppSelector } from 'app/store';
import { selectUserCompanies } from 'app/store/userCompaniesSlice';
import {
  AgaveProject,
  AgaveGlCode,
  AgaveCostCode,
  AgaveCostType,
  AgaveSubJob,
  AgaveVendor,
  AgaveLocation,
  AgaveDepartment,
  AgaveEmployeeEntity,
  AgaveEquipment,
  AgaveEmCostCode,
  AgaveEmCostType,
  AgaveSubaccount,
} from 'API';
import { agaveService } from 'app/services/agave.service';
import { isEmDepartmentScopingEnabled } from 'app/util/emDepartmentScoping';
import { AgaveSourceSystem } from 'app/services/agave/agaveConstants';
import { useAgaveSourceSystem } from 'app/hooks/useAgaveSourceSystem';
import {
  searchProjects as searchProjectsAction,
  loadMoreProjects,
  searchGlCodes as searchGlCodesAction,
  loadMoreGlCodes,
  searchCostCodes as searchCostCodesAction,
  loadMoreCostCodes,
  searchCostTypes as searchCostTypesAction,
  loadMoreCostTypes,
  searchSubJobs as searchSubJobsAction,
  loadMoreSubJobs,
  searchVendors as searchVendorsAction,
  loadMoreVendors,
  searchLocations as searchLocationsAction,
  loadMoreLocations,
  searchDepartments as searchDepartmentsAction,
  loadMoreDepartments,
  searchSubaccounts as searchSubaccountsAction,
  loadMoreSubaccounts,
  searchEquipment as searchEquipmentAction,
  loadMoreEquipment,
  searchEmployees as searchEmployeesAction,
  loadMoreEmployees,
  SearchType,
} from 'app/pages/store/agaveSlice';

interface UseAgaveSearchReturn<T> {
  // State
  searchResults: T[];
  isSearching: boolean;
  isLoadingMore: boolean;
  searchTerm: string | undefined;
  nextToken: string | null;
  hasMore: boolean;

  // Actions
  searchAgaveCode: (term: string) => Promise<void>;
  loadMore: () => Promise<void>;
  clearSearch: () => void;
}

interface UseAgaveSearchOptions {
  dropdownId: string;
  useGlobalState?: boolean;
  searchType: SearchType;
  cardGroupId?: string;
  projectId?: string;
  costCodeId?: string;
  subJobId?: string;
  locationId?: string;
  departmentId?: string;
  employeeId?: string;
  // SEE-1325: the EM cascade. This hook builds its OWN filter for the search and
  // load-more paths, separate from useAgaveCodingDropdown's — so these have to be
  // threaded here too or typing in an EM dropdown searches without them.
  emGroup?: string;
  emCo?: string;
  department?: string;
  emCostCodeId?: string;
}

// SEE-308: `serviceMethod` returns the v2Agave payload directly via the CDK
// AppSync endpoint (no more `result.data.<key>` unwrapping). The legacy
// `dataKey` field stays for any callers still reading it externally.
type AgaveSearchPayload<T> = { items?: T[] | null; nextToken?: string | null; total?: number | null };
// A union, not one type with optional fields: making action/loadMoreAction
// optional everywhere would let an entry that NEEDS them (deleting
// `loadMoreAction: loadMoreVendors`, say) type-check cleanly while load-more
// silently stopped paginating that entity. Only the second arm — the
// filter-dependent EM lists, which never take the Redux load-more branch and
// paginate through fetchLocal instead — may omit them; `never` keeps every
// other entry required at compile time. The runtime `if (!config.loadMoreAction)`
// guard below is the narrowing.
type SearchConfig =
  | {
      serviceMethod: (args: { input: any }) => Promise<AgaveSearchPayload<any>>;
      action: any;
      loadMoreAction: any;
      dataKey: string;
    }
  | {
      serviceMethod: (args: { input: any }) => Promise<AgaveSearchPayload<any>>;
      action?: never;
      loadMoreAction?: never;
      dataKey: string;
    };

export const SEARCH_CONFIGS: Record<SearchType, SearchConfig> = {
  projects: {
    serviceMethod: (args) => agaveService.v2SearchAgaveProjects(args),
    action: searchProjectsAction,
    loadMoreAction: loadMoreProjects,
    dataKey: 'v2SearchAgaveProjects',
  },
  glCodes: {
    serviceMethod: (args) => agaveService.v2SearchAgaveGLCodes(args),
    action: searchGlCodesAction,
    loadMoreAction: loadMoreGlCodes,
    dataKey: 'v2SearchAgaveGLCodes',
  },
  costCodes: {
    serviceMethod: (args) => agaveService.v2SearchAgaveCostCodes(args),
    action: searchCostCodesAction,
    loadMoreAction: loadMoreCostCodes,
    dataKey: 'v2SearchAgaveCostCodes',
  },
  costTypes: {
    serviceMethod: (args) => agaveService.v2SearchAgaveCostTypes(args),
    action: searchCostTypesAction,
    loadMoreAction: loadMoreCostTypes,
    dataKey: 'v2SearchAgaveCostTypes',
  },
  subJobs: {
    serviceMethod: (args) => agaveService.v2SearchAgaveSubJobs(args),
    action: searchSubJobsAction,
    loadMoreAction: loadMoreSubJobs,
    dataKey: 'v2SearchAgaveSubJobs',
  },
  vendors: {
    serviceMethod: (args) => agaveService.v2SearchAgaveVendors(args),
    action: searchVendorsAction,
    loadMoreAction: loadMoreVendors,
    dataKey: 'v2SearchAgaveVendors',
  },
  locations: {
    serviceMethod: (args) => agaveService.v2SearchAgaveLocations(args),
    action: searchLocationsAction,
    loadMoreAction: loadMoreLocations,
    dataKey: 'v2SearchAgaveLocations',
  },
  departments: {
    serviceMethod: (args) => agaveService.v2SearchAgaveDepartments(args),
    action: searchDepartmentsAction,
    loadMoreAction: loadMoreDepartments,
    dataKey: 'v2SearchAgaveDepartments',
  },
  subaccounts: {
    serviceMethod: (args) => agaveService.v2SearchAgaveSubaccounts(args),
    action: searchSubaccountsAction,
    loadMoreAction: loadMoreSubaccounts,
    dataKey: 'v2SearchAgaveSubaccounts',
  },
  equipment: {
    serviceMethod: (args) => agaveService.v2SearchAgaveEquipment(args),
    action: searchEquipmentAction,
    loadMoreAction: loadMoreEquipment,
    dataKey: 'v2SearchAgaveEquipment',
  },
  // SEE-1325: no action/loadMoreAction on purpose. The EM lists are
  // filter-dependent, so search goes through serviceMethod directly and
  // pagination through useAgaveCodingDropdown.fetchLocal — Redux thunks for
  // them would be unreachable (and were, until removed).
  emCostCodes: {
    serviceMethod: (args) => agaveService.v2SearchAgaveEmCostCodes(args),
    dataKey: 'v2SearchAgaveEmCostCodes',
  },
  emCostTypes: {
    serviceMethod: (args) => agaveService.v2SearchAgaveEmCostTypes(args),
    dataKey: 'v2SearchAgaveEmCostTypes',
  },
  employees: {
    serviceMethod: (args) => agaveService.v2SearchAgaveEmployees(args),
    action: searchEmployeesAction,
    loadMoreAction: loadMoreEmployees,
    dataKey: 'v2SearchAgaveEmployees',
  },
};

interface ExecuteAgaveGraphQLRequestParams<T> {
  useDirectRequest: boolean;
  filter: any;
  nextToken: string | null;
  config: SearchConfig;
  dispatch: any;
  createCancellablePromise: (promise: Promise<any>) => Promise<any>;
  isMountedRef: React.MutableRefObject<boolean>;
  searchId: string;
  lastSearchIdRef: React.MutableRefObject<string>;
  setSearchResults: React.Dispatch<React.SetStateAction<T[]>>;
  setNextToken: React.Dispatch<React.SetStateAction<string | null>>;
  setHasMore: React.Dispatch<React.SetStateAction<boolean>>;
  inputOverrides?: any;
}

const executeAgaveGraphQLRequest = async <T>({
  useDirectRequest,
  filter,
  nextToken,
  config,
  dispatch,
  createCancellablePromise,
  isMountedRef,
  searchId,
  lastSearchIdRef,
  setSearchResults,
  setNextToken,
  setHasMore,
  inputOverrides = {},
}: ExecuteAgaveGraphQLRequestParams<T>) => {
  // Different structure for list queries vs search queries
  const isListQuery = config.dataKey.startsWith('list');

  let searchInput: any;

  if (isListQuery) {
    // List queries use flat arguments
    searchInput = {
      ...inputOverrides,
      filter,
      nextToken,
      limit: 1000,
    };
  } else {
    // Search queries use an input object
    searchInput = {
      input: {
        filter,
        nextToken,
        limit: 1000,
      },
      ...inputOverrides,
    };
  }

  if (useDirectRequest) {
    // Direct call via the CDK v2-agave service (SEE-308).
    const searchPromise = config.serviceMethod(searchInput);
    const searchResult = (await createCancellablePromise(searchPromise)) as {
      items: T[];
      nextToken: string | null;
      total?: number;
    } | null;

    if (isMountedRef.current && searchResult && lastSearchIdRef.current === searchId) {
      // Append new results to existing search results
      const newItems = searchResult.items || [];
      setSearchResults((prev: T[]) => [...prev, ...newItems]);
      setNextToken(searchResult.nextToken || null);
      setHasMore(!!searchResult.nextToken);
    }
  } else {
    // Redux action for non-search cases
    // The action creators expect arguments in specific formats based on their definition
    // For list actions, they expect flat args. For search actions, they expect { input: ... } or flat args depending on definition
    // However, the thunks we defined for locations/departments take flat args + dropdownId

    // Every type that can reach this branch has a loadMoreAction; the EM lists
    // (which don't) are filter-dependent and paginate through fetchLocal.
    if (!config.loadMoreAction) return;
    await dispatch(config.loadMoreAction(searchInput));

    // For non-search pagination, we don't need to update local state
    // The global state will be updated by the Redux action
  }
};

export const useAgaveSearch = <
  T extends
    | AgaveProject
    | AgaveGlCode
    | AgaveCostCode
    | AgaveCostType
    | AgaveSubJob
    | AgaveVendor
    | AgaveLocation
    | AgaveDepartment
    | AgaveSubaccount
    | AgaveEmployeeEntity
    | AgaveEquipment
    | AgaveEmCostCode
    | AgaveEmCostType
>({
  dropdownId,
  useGlobalState = false,
  searchType,
  cardGroupId,
  projectId,
  costCodeId,
  subJobId,
  locationId,
  departmentId,
  employeeId,
  emGroup,
  emCo,
  department,
  emCostCodeId,
}: UseAgaveSearchOptions): UseAgaveSearchReturn<T> => {
  const dispatch = useAppDispatch();
  const { selectedPaidol, selectedPaidolId, paidolUsers } = useAppSelector(selectUserCompanies);
  const config = SEARCH_CONFIGS[searchType];

  // Detect the ERP via useAgaveSourceSystem (which reads
  // paidol.agaveSourceSystemSlug, falling back to agaveAccount.source_system)
  // rather than agaveAccount.source_system directly. Direct ERP-connection
  // (erpSpeedchain) paidols never load agaveAccount, so the old check was always
  // false for them — the project/cost-code/location filters silently dropped and
  // the dropdowns showed the full unfiltered list. Legacy Agave clients keep the
  // same behavior via the agaveAccount fallback inside the hook.
  const { isSage300Client, isSageIntacctClient, sourceSystemIn } = useAgaveSourceSystem();
  const isSage300CRE = isSage300Client;
  const isSageIntacct = isSageIntacctClient;
  const isViewpointVista = sourceSystemIn(AgaveSourceSystem.VIEWPOINT_VISTA_HOSTED);
  // Spectrum cost codes are job-scoped too; without it, typing a term dropped
  // projectId and searched the whole paidol (mirrors useAgaveCodingDropdown).
  const isSpectrum = sourceSystemIn(AgaveSourceSystem.SPECTRUM);
  const supportsProjectCostCodeFiltering = isSage300CRE || isSageIntacct || isViewpointVista || isSpectrum;
  // SEE-975: cost-type filtering by cost code is populate-driven for Vista. Only
  // DIRECT (AppXchange, erpSpeedchain) Vista paidols get the AgaveCostCodeCostType
  // join populated by the ingest; legacy Agave-SDK Vista paidols have an empty
  // join, so filtering them would blank the cost-type dropdown. Gate on direct.
  const isDirectVista = isViewpointVista && Boolean((selectedPaidol as any)?.erpSpeedchain);
  const supportsCostTypeCostCodeFiltering =
    supportsProjectCostCodeFiltering && (!isViewpointVista || isDirectVista);
  // SEE-1325: must match `useAgaveCodingDropdown.buildFilterDependentFilter`.
  // That builder serves the browse path and this one the search / load-more
  // paths, for the same two dropdowns — so if they disagree about what belongs
  // in the filter, typing a term silently applies different scoping than opening
  // the list did. Harmless today only because the resolver re-checks the setting
  // and this path skips `makeAgaveCacheKey`; both are exactly the assumptions
  // that made the cache-key divergence this ticket fixed look harmless too.
  const emDepartmentScopingEnabled = isEmDepartmentScopingEnabled(selectedPaidol);

  // Determine company-level restrictions by search type
  const allowedIds = (() => {
    const paidolAny: any = selectedPaidol as any;
    // Find current user's PaidolUser for the selected paidol
    const currentPaidolUser: any = Array.isArray(paidolUsers)
      ? paidolUsers.find((pu) => pu.paidol?.id === selectedPaidolId)
      : undefined;
    // Resolve user-level settings
    const userProject = currentPaidolUser?.projectVisibilitySettings;
    const userGl = currentPaidolUser?.glCodeVisibilitySettings;
    const userLocation = currentPaidolUser?.locationVisibilitySettings;
    const userDepartment = currentPaidolUser?.departmentVisibilitySettings;

    if (searchType === 'projects') {
      // Check if company-level project filtering is enabled
      if (!paidolAny?.projectFilteringByUser?.isEnabled) {
        return undefined; // Filtering disabled at company level, show all
      }

      const userIds: string[] | undefined = Array.isArray(userProject?.ids)
        ? (userProject.ids as string[])
        : undefined;
      const userAll: boolean | undefined = userProject?.isAllAvailable;
      if (userAll === false && userIds && userIds.length > 0) {
        return userIds;
      }
      if (userAll === true) {
        return undefined; // User can see all, no filtering needed
      }
      const paidolProjectSettings = paidolAny?.projectVisibilitySettings;
      if (paidolProjectSettings?.isAllAvailable) {
        return undefined; // Company shows all, no filtering needed
      } else {
        return paidolProjectSettings?.ids as string[] | undefined;
      }
    }

    if (searchType === 'glCodes') {
      // Check if company-level GL code filtering is enabled
      if (!paidolAny?.glCodeFilteringByUser?.isEnabled) {
        return undefined; // Filtering disabled at company level, show all
      }

      const userIds: string[] | undefined = Array.isArray(userGl?.ids) ? (userGl.ids as string[]) : undefined;
      const userAll: boolean | undefined = userGl?.isAllAvailable;
      if (userAll === false && userIds && userIds.length > 0) {
        return userIds;
      }
      if (userAll === true) {
        return undefined; // User can see all, no filtering needed
      }
      const paidolGlCodeSettings = paidolAny?.glCodeVisibilitySettings;
      if (paidolGlCodeSettings?.isAllAvailable) {
        return undefined; // Company shows all, no filtering needed
      } else {
        return paidolGlCodeSettings?.ids as string[] | undefined;
      }
    }

    if (searchType === 'locations') {
      const userIds: string[] | undefined = Array.isArray(userLocation?.ids)
        ? (userLocation.ids as string[])
        : undefined;
      const userAll: boolean | undefined = userLocation?.isAllAvailable;
      if (userAll === false && userIds && userIds.length > 0) {
        return userIds;
      }
      if (userAll === true) {
        return undefined; // User can see all, no filtering needed
      }
      const paidolLocationSettings = paidolAny?.locationVisibilitySettings;
      if (paidolLocationSettings?.isAllAvailable) {
        return undefined; // Company shows all, no filtering needed
      } else {
        return paidolLocationSettings?.ids as string[] | undefined;
      }
    }

    if (searchType === 'departments') {
      const userIds: string[] | undefined = Array.isArray(userDepartment?.ids)
        ? (userDepartment.ids as string[])
        : undefined;
      const userAll: boolean | undefined = userDepartment?.isAllAvailable;
      if (userAll === false && userIds && userIds.length > 0) {
        return userIds;
      }
      if (userAll === true) {
        return undefined; // User can see all, no filtering needed
      }
      const paidolDepartmentSettings = paidolAny?.departmentVisibilitySettings;
      if (paidolDepartmentSettings?.isAllAvailable) {
        return undefined; // Company shows all, no filtering needed
      } else {
        return paidolDepartmentSettings?.ids as string[] | undefined;
      }
    }

    return undefined;
  })();

  // Local state for search results and pagination
  const [searchResults, setSearchResults] = useState<T[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [searchTerm, setSearchTerm] = useState<string | undefined>(undefined);
  const [nextToken, setNextToken] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(false);

  // Refs for managing search state
  const searchInProgressRef = useRef(false);
  const lastSearchTermRef = useRef<string | undefined>(undefined);
  const isMountedRef = useRef(true);
  const lastSearchIdRef = useRef<string>('');
  const abortControllerRef = useRef<AbortController | null>(null);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      isMountedRef.current = false;
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
        abortControllerRef.current = null;
      }
    };
  }, []);

  // Clear search state when filters change so stale results from a previous filter context aren't shown
  useEffect(() => {
    // Cancel any in-flight requests
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
    // Clear all search state
    setSearchTerm(undefined);
    setSearchResults([]);
    setNextToken(null);
    setHasMore(false);
    searchInProgressRef.current = false;
    lastSearchTermRef.current = undefined;
    // SEE-1325: the EM cascade values are here for the same reason costCodeId
    // is — changing equipment (emGroup/emCo/department) or the EM cost code
    // must drop the previous context's search results, not keep serving them.
  }, [projectId, costCodeId, subJobId, locationId, employeeId, emGroup, emCo, department, emCostCodeId]);

  const createCancellablePromise = useCallback(
    <TPayload>(searchPromise: Promise<TPayload>): Promise<TPayload> => {
      let abortHandler: (() => void) | null = null;

      const racePromise = Promise.race([
        searchPromise,
        new Promise<never>((_, reject) => {
          abortHandler = () => {
            reject(new Error('Request cancelled'));
          };
          if (abortControllerRef.current?.signal) {
            abortControllerRef.current.signal.addEventListener('abort', abortHandler);
          }
        }),
      ]);

      return racePromise.finally(() => {
        // Always clean up the event listener, regardless of how the promise settles
        if (abortHandler && abortControllerRef.current?.signal) {
          abortControllerRef.current.signal.removeEventListener('abort', abortHandler);
        }
      });
    },
    []
  );

  const searchAgaveCode = useCallback(
    async (term: string) => {
      if (!isMountedRef.current) {
        return;
      }

      // Prevent duplicate searches
      if (term === lastSearchTermRef.current) {
        return;
      }
      if (searchInProgressRef.current) {
        return;
      }
      if (!selectedPaidol?.id) {
        return;
      }

      // Cancel any existing request
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }

      // Create new AbortController for this request
      abortControllerRef.current = new AbortController();

      // Generate unique search ID for this request
      const searchId = `${dropdownId}-${Date.now()}-${Math.random()}`;
      lastSearchIdRef.current = searchId;

      lastSearchTermRef.current = term;
      setSearchTerm(term);
      searchInProgressRef.current = true;
      setIsSearching(true);

      try {
        const isListQuery = config.dataKey.startsWith('list');
        let filter: any;
        let inputOverrides: any = {};

        // FIRST: Initialize filter based on query type
        if (isListQuery) {
          // List queries: paidolId goes in inputOverrides
          inputOverrides = {
            paidolId: selectedPaidol.id,
          };
          // For list queries, filter is only for search term
          if (term && !term.startsWith('FORCE_REFRESH_')) {
            filter = { name: { contains: term } };
          } else {
            filter = {};
          }
        } else {
          // Search queries: paidolId goes in filter
          filter = {
            paidolId: selectedPaidol.id,
          };
        }

        // SECOND: Add conditional filters
        // Include cardGroupId if provided
        if (cardGroupId) {
          filter.cardGroupId = cardGroupId;
        }

        // For subJobs, add projectId and costCodeId filters
        if (searchType === 'subJobs') {
          filter.projectId = projectId || undefined;
          filter.costCodeId = costCodeId || undefined;
        }

        // For costCodes, add subJobId and/or projectId filters (only for Sage 300 CRE and Sage Intacct)
        // QBO doesn't support project-based cost code filtering
        if (searchType === 'costCodes' && supportsProjectCostCodeFiltering) {
          if (subJobId) {
            filter.subJobId = subJobId;
          }
          if (projectId) {
            filter.projectId = projectId;
          }
        }

        // For costTypes, add projectId and costCodeId filters (only for supported ERPs).
        // SEE-975: DIRECT Vista is included now that the ingest populates its
        // CostCodeCostType join; legacy Agave-SDK Vista stays excluded (empty join
        // would blank the dropdown). See supportsCostTypeCostCodeFiltering.
        if (searchType === 'costTypes' && supportsCostTypeCostCodeFiltering) {
          if (projectId) {
            filter.projectId = projectId;
          }
          if (costCodeId) {
            filter.costCodeId = costCodeId;
          }
        }

        // SEE-944: for projects, narrow search to the selected location so typing
        // a name stays within the locked location filter — matching the default
        // (non-search) list. Without this, project search returns every project
        // regardless of location, letting a user pick a project outside it.
        if (searchType === 'projects' && locationId) {
          filter.locationId = locationId;
        }

        // SEE-1325: the EM cascade. `emGroup` is REQUIRED -- the resolver returns
        // nothing without it, so omitting it here made every search in these two
        // dropdowns come back empty while opening them worked fine.
        if (searchType === 'emCostCodes' || searchType === 'emCostTypes') {
          filter.emGroup = emGroup;
          // `emGroup` is unconditional -- it is the cascade key, not part of the
          // department opt-in. `emCo`/`department` are the scoping pair and only
          // go out when the paidol has opted in.
          if (emDepartmentScopingEnabled) {
            if (emCo) filter.emCo = emCo;
            if (department) filter.department = department;
          }
          if (searchType === 'emCostTypes' && emCostCodeId) {
            filter.emCostCodeId = emCostCodeId;
          }
        }

        // SEE-808: Sage Intacct location/department/employee dropdowns show full
        // lists. Project-driven auto-fill (fetchProjectAutoFillDimensions) handles
        // the single-match convenience case for location/department.

        // Only include searchTerm if it's a real search (not a refresh) and not a list query
        if (term && !term.startsWith('FORCE_REFRESH_') && !isListQuery) {
          filter.searchTerm = term;
        }

        // Include company-level restrictions if available
        if (Array.isArray(allowedIds) && allowedIds.length) {
          filter.ids = allowedIds as string[];
        }

        // Construct input based on query type
        let inputArgs: any;

        if (isListQuery) {
          inputArgs = {
            ...inputOverrides,
            filter,
            limit: 25,
          };
        } else {
          inputArgs = {
            input: {
              filter,
              limit: 25,
            },
          };
        }

        const searchPromise = config.serviceMethod(inputArgs);
        const searchResult = (await createCancellablePromise(searchPromise)) as {
          items: T[];
          nextToken: string | null;
          total?: number;
        } | null;

        // Only update results if this is still the most recent search for this dropdown
        if (isMountedRef.current && searchResult && lastSearchIdRef.current === searchId) {
          setSearchResults(searchResult.items || []);
          setNextToken(searchResult.nextToken || null);
          setHasMore(!!searchResult.nextToken);
        }
      } catch (error) {
        // Only log error if it's not a cancellation
        if (error instanceof Error && error.message !== 'Request cancelled') {
          // Search failed silently
        }
      } finally {
        if (isMountedRef.current) {
          searchInProgressRef.current = false;
          setIsSearching(false);
        }
      }
    },
    [
      selectedPaidol?.id,
      dropdownId,
      dispatch,
      createCancellablePromise,
      config,
      allowedIds,
      cardGroupId,
      projectId,
      costCodeId,
      subJobId,
      locationId,
      searchType,
      supportsProjectCostCodeFiltering,
      supportsCostTypeCostCodeFiltering,
      // SEE-1325: the EM cascade. Without these the callback is memoised with
      // the values from FIRST render — equipment is usually picked after mount,
      // so a search sent filter.emGroup = undefined (empty dropdown), and a
      // stale emCostCodeId dropped the pair constraint, offering combinations
      // Vista rejects at AP entry.
      emGroup,
      emCo,
      department,
      emCostCodeId,
      emDepartmentScopingEnabled,
    ]
  );

  const loadMore = useCallback(async () => {
    if (!selectedPaidol?.id || isLoadingMore) {
      return;
    }

    const isListQuery = config.dataKey.startsWith('list');
    let filter: any;
    let inputOverrides: any = {};

    // FIRST: Initialize filter based on query type
    if (isListQuery) {
      // List queries: paidolId goes in inputOverrides
      inputOverrides = {
        paidolId: selectedPaidol.id,
      };
      // For list queries, filter is only for search term
      if (searchTerm) {
        filter = { name: { contains: searchTerm } };
      } else {
        filter = {}; // Initialize empty filter for list queries without search term
      }
    } else {
      // Search queries: paidolId goes in filter
      filter = {
        paidolId: selectedPaidol.id,
      };
    }

    // SECOND: Add conditional filters to the initialized filter
    // Include cardGroupId if provided
    if (cardGroupId) {
      filter.cardGroupId = cardGroupId;
    }

    // For subJobs, add projectId and costCodeId filters
    if (searchType === 'subJobs') {
      filter.projectId = projectId || undefined;
      filter.costCodeId = costCodeId || undefined;
    }

    // For costCodes, add subJobId and/or projectId filters (Sage 300 CRE, Sage Intacct, Viewpoint Vista)
    // QBO doesn't support project-based cost code filtering
    if (searchType === 'costCodes' && supportsProjectCostCodeFiltering) {
      if (subJobId) {
        filter.subJobId = subJobId;
      }
      if (projectId) {
        filter.projectId = projectId;
      }
    }

    // For costTypes, add projectId and costCodeId filters (only for supported ERPs).
    // Direct Vista included / legacy Vista excluded — see the search branch above.
    if (searchType === 'costTypes' && supportsCostTypeCostCodeFiltering) {
      if (projectId) {
        filter.projectId = projectId;
      }
      if (costCodeId) {
        filter.costCodeId = costCodeId;
      }
    }

    // SEE-944: keep project pagination scoped to the locked location, matching
    // the search branch above — otherwise pages past the first slip past the filter.
    if (searchType === 'projects' && locationId) {
      filter.locationId = locationId;
    }

    // SEE-1325: the EM cascade. `emGroup` is REQUIRED -- the resolver returns
    // nothing without it, so omitting it here made every search in these two
    // dropdowns come back empty while opening them worked fine.
    if (searchType === 'emCostCodes' || searchType === 'emCostTypes') {
      filter.emGroup = emGroup;
      // Same gate as the search branch above -- pages past the first must carry
      // identical scoping, or load-more widens the list mid-scroll.
      if (emDepartmentScopingEnabled) {
        if (emCo) filter.emCo = emCo;
        if (department) filter.department = department;
      }
      if (searchType === 'emCostTypes' && emCostCodeId) {
        filter.emCostCodeId = emCostCodeId;
      }
    }

    // SEE-808: Sage Intacct location/department/employee dropdowns show full
    // lists. Project-driven auto-fill handles the single-match convenience case
    // for location/department.

    // Include search term if we're currently searching (but not for list queries, already handled above)
    if (searchTerm !== undefined && !isListQuery) {
      filter.searchTerm = searchTerm || undefined;
    }

    // Include company-level restrictions if available
    if (Array.isArray(allowedIds) && allowedIds.length) {
      filter.ids = allowedIds as string[];
    }

    if (!nextToken) {
      return;
    }

    // Cancel any existing request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    // Create new AbortController for this request
    abortControllerRef.current = new AbortController();

    // Generate unique search ID for this pagination request
    const searchId = `${dropdownId}-${Date.now()}-${Math.random()}`;
    lastSearchIdRef.current = searchId;

    setIsLoadingMore(true);

    try {
      // Determine whether to use direct GraphQL request or Redux action
      const useDirectRequest = searchTerm !== undefined;
      const shouldExecute = useDirectRequest || useGlobalState;

      if (shouldExecute) {
        await executeAgaveGraphQLRequest({
          useDirectRequest,
          filter,
          nextToken,
          config,
          dispatch,
          createCancellablePromise,
          isMountedRef,
          searchId,
          lastSearchIdRef,
          setSearchResults,
          setNextToken,
          setHasMore,
          inputOverrides, // Fixed: was searchInputOverrides
        });
      }
    } catch (error) {
      if (error instanceof Error && error.message !== 'Request cancelled') {
        // Handle error silently
      }
    } finally {
      if (isMountedRef.current) {
        setIsLoadingMore(false);
      }
    }
  }, [
    selectedPaidol?.id,
    isLoadingMore,
    searchTerm,
    nextToken,
    dropdownId,
    dispatch,
    createCancellablePromise,
    useGlobalState,
    config,
    setSearchResults,
    setNextToken,
    setHasMore,
    cardGroupId,
    projectId,
    costCodeId,
    subJobId,
    locationId,
    searchType,
    allowedIds,
    supportsProjectCostCodeFiltering,
    supportsCostTypeCostCodeFiltering,
    // SEE-1325: same staleness as searchAgaveCode — pages past the first must
    // carry the cascade the current equipment/cost code implies, not the
    // first render's.
    emGroup,
    emCo,
    department,
    emCostCodeId,
    emDepartmentScopingEnabled,
  ]);

  const clearSearch = useCallback(() => {
    setSearchTerm(undefined);
    setSearchResults([]);
    setNextToken(null);
    setHasMore(false);
    lastSearchTermRef.current = undefined;
  }, []);

  return {
    // State
    searchResults,
    isSearching,
    isLoadingMore,
    searchTerm,
    nextToken,
    hasMore,
    // Actions
    searchAgaveCode,
    loadMore,
    clearSearch,
  };
};
